import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Refresh from '@mui/icons-material/Refresh';
import CloudSync from '@mui/icons-material/CloudSync';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  getOflyncCashflowLogs,
  triggerOflyncCashflowSync,
  type OflyncCashflowSyncLog,
} from '../../../services/request/Reports';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { EmptyState, PageHeader, SectionPanel } from '../../../shared-components/ui';

function statusColor(status: string): 'success' | 'error' | 'warning' | 'default' {
  const s = (status || '').toUpperCase();
  if (s === 'SUCCESS') return 'success';
  if (s === 'FAILED') return 'error';
  if (s === 'SKIPPED') return 'warning';
  return 'default';
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const OflyncCashflowSync: React.FC = () => {
  const [rows, setRows] = useState<OflyncCashflowSyncLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState(() => {
    const n = new Date();
    const prev = new Date(n.getFullYear(), n.getMonth() - 1, 1);
    return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  });
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getOflyncCashflowLogs(page, rowsPerPage);
      const payload = res.data;
      setRows(payload?.result ?? []);
      setTotal(payload?.totalCount ?? 0);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load Oflync cashflow logs.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      const res = await triggerOflyncCashflowSync(period);
      const row = res.data;
      if (row?.status === 'SUCCESS') {
        toast.success(`Synced ${row.period} (${row.httpStatus})`);
      } else if (row?.status === 'SKIPPED') {
        toast.warn(row.errorMessage || `Skipped ${row.period}`);
      } else {
        toast.error(row?.errorMessage || 'Sync failed');
      }
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box>
      <ToastContainer position="top-right" autoClose={4000} />
      <PageHeader
        eyebrow="Lending · Reports"
        title="Oflync cashflow"
        subtitle="The month-end job posts at 23:10 on the last day. Manual sync should use a month that already has interest, repayments, or new debits — the current month is often empty before month-end."
        actions={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              size="small"
              label="Period"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 170 }}
            />
            <Button
              variant="contained"
              startIcon={<CloudSync />}
              onClick={() => void handleSync()}
              disabled={syncing || !period}
            >
              {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => void load()} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SectionPanel
        title="Transaction log"
        subtitle="Scheduled (23:10 last day of month) and manual runs"
      >
        {loading && rows.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No syncs yet"
            description="Run Sync now, or wait for the last-day-of-month job. Enable oflync.cashflow in lead-service and set API key + outlet id."
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>Triggered</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Trigger</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Inflow</TableCell>
                    <TableCell align="right">Outflow</TableCell>
                    <TableCell>HTTP</TableCell>
                    <TableCell>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const open = openId === r.id;
                    return (
                      <React.Fragment key={r.id}>
                        <TableRow hover>
                          <TableCell>
                            <IconButton size="small" onClick={() => setOpenId(open ? null : r.id)}>
                              <ExpandMore
                                fontSize="small"
                                sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>{formatWhen(r.triggeredAt)}</TableCell>
                          <TableCell>{r.period}</TableCell>
                          <TableCell>{r.triggerType}</TableCell>
                          <TableCell>
                            <Chip size="small" label={r.status} color={statusColor(r.status)} variant="outlined" />
                          </TableCell>
                          <TableCell align="right">{formatCurrency(Number(r.inflow) || 0)}</TableCell>
                          <TableCell align="right">{formatCurrency(Number(r.outflow) || 0)}</TableCell>
                          <TableCell>{r.httpStatus ?? '—'}</TableCell>
                          <TableCell>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                              {r.externalReference || '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 0, border: 0 }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <Box sx={{ px: 2, py: 1.5, bgcolor: 'background.default' }}>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  Interest {formatCurrency(Number(r.interestInflow) || 0)} · Repayment{' '}
                                  {formatCurrency(Number(r.repaymentInflow) || 0)} · Late fee{' '}
                                  {formatCurrency(Number(r.lateFeeInflow) || 0)} · Debits{' '}
                                  {formatCurrency(Number(r.debitOutflow) || 0)} · Top-up{' '}
                                  {formatCurrency(Number(r.topUpOutflow) || 0)}
                                  {r.durationMs != null ? ` · ${r.durationMs} ms` : ''}
                                </Typography>
                                {r.errorMessage && (
                                  <Alert severity="warning" sx={{ mb: 1 }}>
                                    {r.errorMessage}
                                  </Alert>
                                )}
                                <Typography variant="caption" color="text.secondary">
                                  Request
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    mb: 1,
                                    p: 1,
                                    fontSize: 12,
                                    overflow: 'auto',
                                    maxHeight: 180,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {r.requestPayload || '—'}
                                </Box>
                                <Typography variant="caption" color="text.secondary">
                                  Response
                                </Typography>
                                <Box
                                  component="pre"
                                  sx={{
                                    m: 0,
                                    p: 1,
                                    fontSize: 12,
                                    overflow: 'auto',
                                    maxHeight: 180,
                                    bgcolor: 'background.paper',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {r.responseBody || '—'}
                                </Box>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 20, 50]}
            />
          </>
        )}
      </SectionPanel>
    </Box>
  );
};

export default OflyncCashflowSync;
